import { Children, forwardRef, isValidElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MutableRefObject, KeyboardEvent as ReactKeyboardEvent, ReactNode, Ref, UIEvent } from 'react';
import { ArrowDownward, ArrowDropDown, ArrowUpward, Close, Search } from '@mui/icons-material';
import MenuList from '@mui/material/MenuList';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { ARROW_DOWN_KEY, ARROW_UP_KEY, ESCAPE_KEY } from 'common/constants/general';
import { useActiveNavigationIndicator, useScrollHiddenItemCounts } from 'components/ui/hooks';
import { OverflowTooltip } from 'components/ui/OverflowTooltip';
import { Skeleton } from 'components/ui/Skeleton';
import { Tooltip } from 'components/ui/Tooltip';
import { getCssPx, getPortalZoomScale } from 'components/ui/utilities/zoom';
import { DEFAULT_ZOOM_LEVEL } from 'context/ZoomContext';
import { PrismThemeProvider } from 'theme';
import {
  PrimaryNavigationChipProps,
  PrimaryNavigationChipSkeletonProps,
  PrimaryNavigationIconButtonProps,
  PrimaryNavigationMenuActionProps,
  PrimaryNavigationMenuItemLeadingActionProps,
  PrimaryNavigationMenuItemProps,
  PrimaryNavigationMenuProps,
  PrimaryNavigationMenuSearchProps,
  PrimaryNavigationMenuShowMoreItemProps,
  PrimaryNavigationProps,
  PrimaryNavigationSearchButtonProps,
  PrimaryNavigationSkeletonProps,
} from './PrimaryNavigation.props';
import {
  PRIMARY_NAVIGATION_SEARCH_BUTTON_WIDTH_PX,
  PrimaryNavigationActions,
  PrimaryNavigationActiveIndicator,
  PrimaryNavigationBrand,
  PrimaryNavigationBrandSlot,
  PrimaryNavigationChipDropdownIcon,
  PrimaryNavigationChipIcon,
  PrimaryNavigationChipLabel,
  PrimaryNavigationChipRoot,
  PrimaryNavigationChipValue,
  PrimaryNavigationContext,
  PrimaryNavigationIconButtonRoot,
  PrimaryNavigationMenuActionContent,
  PrimaryNavigationMenuActionFrame,
  PrimaryNavigationMenuActionIcon,
  PrimaryNavigationMenuActionRoot,
  PrimaryNavigationMenuAiIconRoot,
  PrimaryNavigationMenuItemContent,
  PrimaryNavigationMenuItemEndIcon,
  PrimaryNavigationMenuItemLeadingActionRoot,
  PrimaryNavigationMenuItemLeadingSlot,
  PrimaryNavigationMenuItemRoot,
  PrimaryNavigationMenuRoot,
  PrimaryNavigationMenuSearchClearButton,
  PrimaryNavigationMenuSearchIcon,
  PrimaryNavigationMenuSearchRoot,
  PrimaryNavigationRoot,
  PrimaryNavigationSearchButtonRoot,
  PrimaryNavigationSkeletonTabs,
  PrimaryNavigationTab,
  PrimaryNavigationTabLabel,
  PrimaryNavigationTabs,
  PrimaryNavigationTabsFrame,
} from './PrimaryNavigation.styles';
import { PRIMARY_NAVIGATION_TEST_IDS } from './PrimaryNavigation.testIds';

const createSkeletonItems = (count: number) => Array.from({ length: count }, (_, index) => index);
const getTextTooltipTitle = (content: ReactNode) => (typeof content === 'string' ? content : '');
const PRIMARY_NAVIGATION_MENU_ACTION_SELECTOR = '[data-primary-navigation-menu-action="true"]';
const PRIMARY_NAVIGATION_MENU_CHILD_KIND = {
  action: 'action',
  search: 'search',
} as const;
const PRIMARY_NAVIGATION_MENU_ITEM_SELECTOR = '[data-primary-navigation-menu-scroll-item="true"]';
const PRIMARY_NAVIGATION_MENU_SEARCH_INPUT_SELECTOR = '[data-primary-navigation-menu-search-input="true"]';
const PRIMARY_NAVIGATION_TABS_SCROLLER_SELECTOR = '.MuiTabs-scroller';
const PRIMARY_NAVIGATION_MENU_SCROLL_HINT_LABELS = {
  plural: 'items',
  singular: 'item',
} as const;

const getPrimaryNavigationSkeletonSx = (theme: Theme) => ({
  backgroundColor: alpha(theme.palette.navigation.primary.chip.value.strong, 0.24),
  '&::after': {
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.navigation.primary.chip.value.strong,
      0.24
    )}, transparent)`,
  },
});

type PrimaryNavigationMenuChildKind = typeof PRIMARY_NAVIGATION_MENU_CHILD_KIND[
  keyof typeof PRIMARY_NAVIGATION_MENU_CHILD_KIND
];

type PrimaryNavigationMenuChildComponent = {
  menuChildKind?: PrimaryNavigationMenuChildKind;
};

type PrimaryNavigationMenuAnchorOrigin = NonNullable<PrimaryNavigationMenuProps['anchorOrigin']>;

const getMenuScrollHintLabel = (count: number, singularLabel: string, pluralLabel: string) =>
  `${count} ${count === 1 ? singularLabel : pluralLabel}`;

const getAutoMenuHorizontalPlacement = (
  anchorEl: PrimaryNavigationMenuProps['anchorEl'],
  menuMaxWidth: number,
  menuMinWidth: number,
  zoomLevel: number
) => {
  const resolvedAnchorEl = typeof anchorEl === 'function' ? anchorEl() : anchorEl;

  if (typeof window === 'undefined' || !(resolvedAnchorEl instanceof HTMLElement)) {
    return 'left';
  }

  const anchorRect = resolvedAnchorEl.getBoundingClientRect();
  const menuScale = getPortalZoomScale(zoomLevel);
  const expectedMenuWidth = Math.max(menuMinWidth * menuScale, Math.min(menuMaxWidth * menuScale, window.innerWidth));
  const spaceToRight = window.innerWidth - anchorRect.left;
  const spaceToLeft = anchorRect.right;

  if (spaceToRight >= expectedMenuWidth) {
    return 'left';
  }

  if (spaceToLeft >= expectedMenuWidth) {
    return 'right';
  }

  return spaceToRight >= spaceToLeft ? 'left' : 'right';
};

const getMenuAnchorOffset = (
  size: number,
  origin: PrimaryNavigationMenuAnchorOrigin['horizontal'] | PrimaryNavigationMenuAnchorOrigin['vertical']
) => {
  if (typeof origin === 'number') {
    return origin;
  }

  if (origin === 'right' || origin === 'bottom') {
    return size;
  }

  return origin === 'center' ? size / 2 : 0;
};

const getMenuAnchorPosition = (
  anchorEl: PrimaryNavigationMenuProps['anchorEl'],
  anchorOrigin: PrimaryNavigationMenuAnchorOrigin
): PrimaryNavigationMenuProps['anchorPosition'] | undefined => {
  const resolvedAnchorEl = typeof anchorEl === 'function' ? anchorEl() : anchorEl;

  if (typeof window === 'undefined' || !(resolvedAnchorEl instanceof HTMLElement)) {
    return undefined;
  }

  const anchorRect = resolvedAnchorEl.getBoundingClientRect();

  return {
    left: anchorRect.left + getMenuAnchorOffset(anchorRect.width, anchorOrigin.horizontal),
    top: anchorRect.top + getMenuAnchorOffset(anchorRect.height, anchorOrigin.vertical),
  };
};

const setRef = <TElement,>(ref: Ref<TElement> | undefined, value: TElement | null) => {
  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  if (ref) {
    const mutableRef = ref as MutableRefObject<TElement | null>;
    mutableRef.current = value;
  }
};

const isFocusableMenuElement = (element: HTMLElement) => (
  element.getClientRects().length > 0
  && !element.hasAttribute('aria-disabled')
  && !(element instanceof HTMLButtonElement && element.disabled)
);

const getMenuPaper = (element: HTMLElement) => element.closest<HTMLElement>('.MuiPaper-root');

const getFocusableMenuItems = (paperElement: HTMLElement | null) => {
  if (!paperElement) {
    return [];
  }

  return Array.from(paperElement.querySelectorAll<HTMLElement>(PRIMARY_NAVIGATION_MENU_ITEM_SELECTOR)).filter(
    isFocusableMenuElement
  );
};

const focusMenuElement = (element: HTMLElement | null | undefined) => {
  if (!element || !isFocusableMenuElement(element)) {
    return false;
  }

  element.focus();
  element.scrollIntoView({ block: 'nearest' });
  return true;
};

const getPrimaryNavigationMenuChildKind = (child: ReactNode) => (
  isValidElement(child)
    ? (child.type as PrimaryNavigationMenuChildComponent).menuChildKind
    : undefined
);

const splitPrimaryNavigationMenuChildren = (children: ReactNode) => {
  const bodyChildren: ReactNode[] = [];
  const footerChildren: ReactNode[] = [];
  const headerChildren: ReactNode[] = [];

  Children.forEach(children, child => {
    const childKind = getPrimaryNavigationMenuChildKind(child);

    if (childKind === PRIMARY_NAVIGATION_MENU_CHILD_KIND.search) {
      headerChildren.push(child);
      return;
    }

    if (childKind === PRIMARY_NAVIGATION_MENU_CHILD_KIND.action) {
      footerChildren.push(child);
      return;
    }

    bodyChildren.push(child);
  });

  return {
    bodyChildren,
    footerChildren,
    headerChildren,
  };
};

export const PrimaryNavigationChip = forwardRef<HTMLDivElement, PrimaryNavigationChipProps>(
  (
    {
      children,
      icon,
      label,
      showDropdownIcon = true,
      value,
      valueTone = 'default',
      zoomLevel = DEFAULT_ZOOM_LEVEL,
      ...props
    },
    ref
  ) => {
    const hasStructuredContent = icon || label || value;

    return (
      <PrimaryNavigationChipRoot ref={ref} zoomLevel={zoomLevel} {...props}>
        {hasStructuredContent ? (
          <>
            {icon && <PrimaryNavigationChipIcon zoomLevel={zoomLevel}>{icon}</PrimaryNavigationChipIcon>}
            {label && <PrimaryNavigationChipLabel zoomLevel={zoomLevel}>{label}</PrimaryNavigationChipLabel>}
            {value && (
              <OverflowTooltip title={getTextTooltipTitle(value)}>
                <PrimaryNavigationChipValue valueTone={valueTone} zoomLevel={zoomLevel}>
                  {value}
                </PrimaryNavigationChipValue>
              </OverflowTooltip>
            )}
            {showDropdownIcon && (
              <PrimaryNavigationChipDropdownIcon zoomLevel={zoomLevel}>
                <ArrowDropDown />
              </PrimaryNavigationChipDropdownIcon>
            )}
          </>
        ) : (
          children
        )}
      </PrimaryNavigationChipRoot>
    );
  }
);

PrimaryNavigationChip.displayName = 'PrimaryNavigationChip';

export const PrimaryNavigationIconButton = ({
  isInteractive = false,
  zoomLevel = DEFAULT_ZOOM_LEVEL,
  ...props
}: PrimaryNavigationIconButtonProps) => (
  <PrimaryNavigationIconButtonRoot isInteractive={isInteractive} zoomLevel={zoomLevel} {...props} />
);

export const PrimaryNavigationSearchButton = ({
  zoomLevel = DEFAULT_ZOOM_LEVEL,
  ...props
}: PrimaryNavigationSearchButtonProps) => (
  <PrimaryNavigationSearchButtonRoot zoomLevel={zoomLevel} {...props} />
);

export const PrimaryNavigationMenu = ({
  anchorEl,
  anchorPosition,
  anchorReference,
  anchorOrigin,
  children,
  autoFocus,
  disableAutoFocusItem,
  horizontalPlacement = 'left',
  menuMaxWidth = 320,
  menuMinWidth = 200,
  menuWidth = 'default',
  MenuListProps,
  open,
  PaperProps,
  scrollHintItemLabelPlural = PRIMARY_NAVIGATION_MENU_SCROLL_HINT_LABELS.plural,
  scrollHintItemLabelSingular = PRIMARY_NAVIGATION_MENU_SCROLL_HINT_LABELS.singular,
  transformOrigin,
  zoomLevel = DEFAULT_ZOOM_LEVEL,
  ...props
}: PrimaryNavigationMenuProps) => {
  const menuListRef = useRef<HTMLUListElement>(null);
  const {
    ref: externalPaperRef,
    ...paperProps
  } = PaperProps ?? {};
  const {
    onKeyDown: externalMenuListOnKeyDown,
    onScroll: externalMenuListOnScroll,
    ref: externalMenuListRef,
    ...menuListProps
  } = MenuListProps ?? {};
  const {
    bodyChildren,
    footerChildren,
    headerChildren,
  } = splitPrimaryNavigationMenuChildren(children);
  const bodyChildCount = bodyChildren.length;
  const footerChildCount = footerChildren.length;
  const headerChildCount = headerChildren.length;
  const shouldAutoAnchorPosition = !anchorReference && !anchorPosition;
  const [, forceAnchorPositionUpdate] = useState(0);
  const { hiddenItemCounts, updateHiddenItemCounts } = useScrollHiddenItemCounts<HTMLUListElement>({
    isEnabled: Boolean(open),
    itemSelector: PRIMARY_NAVIGATION_MENU_ITEM_SELECTOR,
    scrollContainerRef: menuListRef,
  });
  const scrollHintAboveLabel = hiddenItemCounts.above > 0
    ? `↑ ${getMenuScrollHintLabel(
      hiddenItemCounts.above,
      scrollHintItemLabelSingular,
      scrollHintItemLabelPlural
    )}`
    : undefined;
  const scrollHintBelowLabel = hiddenItemCounts.below > 0
    ? `↓ ${getMenuScrollHintLabel(
      hiddenItemCounts.below,
      scrollHintItemLabelSingular,
      scrollHintItemLabelPlural
    )}`
    : undefined;
  const resolvedHorizontalPlacement = horizontalPlacement === 'auto'
    ? getAutoMenuHorizontalPlacement(anchorEl, menuMaxWidth, menuMinWidth, zoomLevel)
    : horizontalPlacement;
  const resolvedAnchorOrigin = anchorOrigin ?? {
    horizontal: resolvedHorizontalPlacement,
    vertical: 'bottom',
  };
  const resolvedTransformOrigin = transformOrigin ?? {
    horizontal: resolvedHorizontalPlacement,
    vertical: 'top',
  };
  // MUI Popover can drift when the anchor lives inside the CSS-zoomed app root.
  // Capture viewport coordinates from the trigger so clicks on chip edges keep menus aligned.
  const resolvedAnchorPosition = anchorPosition
    ?? (shouldAutoAnchorPosition ? getMenuAnchorPosition(anchorEl, resolvedAnchorOrigin) : undefined);
  const resolvedAnchorReference = anchorReference
    ?? (resolvedAnchorPosition ? 'anchorPosition' : undefined);

  const handlePaperRef = useCallback((node: HTMLDivElement | null) => {
    setRef(externalPaperRef, node);
  }, [externalPaperRef]);

  const handleMenuListRef = useCallback((node: HTMLUListElement | null) => {
    menuListRef.current = node;
    setRef(externalMenuListRef, node);
  }, [externalMenuListRef]);

  const handleMenuListScroll = useCallback((event: UIEvent<HTMLUListElement>) => {
    externalMenuListOnScroll?.(event);
    updateHiddenItemCounts();
  }, [externalMenuListOnScroll, updateHiddenItemCounts]);

  // Recalculate hidden-row hints when the menu opens or one of its sections changes.
  useEffect(() => {
    if (!open) {
      return;
    }

    updateHiddenItemCounts();
  }, [bodyChildCount, footerChildCount, headerChildCount, open, updateHiddenItemCounts]);

  // Keep coordinate-based anchors synced when resize/scroll changes the trigger's viewport rect.
  useEffect(() => {
    if (!open || !shouldAutoAnchorPosition || typeof window === 'undefined') {
      return undefined;
    }

    let animationFrameId: number | null = null;

    const requestAnchorPositionUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        forceAnchorPositionUpdate(revision => revision + 1);
      });
    };

    window.addEventListener('resize', requestAnchorPositionUpdate);
    window.addEventListener('scroll', requestAnchorPositionUpdate, true);
    window.visualViewport?.addEventListener('resize', requestAnchorPositionUpdate);
    window.visualViewport?.addEventListener('scroll', requestAnchorPositionUpdate);

    return () => {
      window.removeEventListener('resize', requestAnchorPositionUpdate);
      window.removeEventListener('scroll', requestAnchorPositionUpdate, true);
      window.visualViewport?.removeEventListener('resize', requestAnchorPositionUpdate);
      window.visualViewport?.removeEventListener('scroll', requestAnchorPositionUpdate);

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [open, shouldAutoAnchorPosition]);

  const handleMenuListKeyDown = useCallback((event: ReactKeyboardEvent<HTMLUListElement>) => {
    const isVerticalNavigationKey = event.key === ARROW_DOWN_KEY || event.key === ARROW_UP_KEY;
    externalMenuListOnKeyDown?.(event);

    // MUI MenuList handles ArrowUp/ArrowDown before calling this prop handler and marks
    // the event as prevented; still run our split header/body/footer focus handoff.
    if (event.defaultPrevented && !isVerticalNavigationKey) {
      return;
    }

    const target = event.target as HTMLElement | null;
    const paperElement = target ? getMenuPaper(target) : null;
    const currentItem = target?.closest<HTMLElement>(PRIMARY_NAVIGATION_MENU_ITEM_SELECTOR);
    const menuItems = getFocusableMenuItems(paperElement);
    const currentItemIndex = currentItem ? menuItems.indexOf(currentItem) : -1;

    if (!currentItem || currentItemIndex === -1) {
      return;
    }

    if (event.key === ARROW_DOWN_KEY) {
      const nextItem = menuItems[currentItemIndex + 1];
      const actionButton = currentItemIndex === menuItems.length - 1
        ? paperElement?.querySelector<HTMLElement>(PRIMARY_NAVIGATION_MENU_ACTION_SELECTOR)
        : null;

      if (focusMenuElement(nextItem ?? actionButton)) {
        event.preventDefault();
      }
      return;
    }

    if (event.key === ARROW_UP_KEY) {
      const previousItem = menuItems[currentItemIndex - 1];
      const searchInput = currentItemIndex === 0
        ? paperElement?.querySelector<HTMLInputElement>(PRIMARY_NAVIGATION_MENU_SEARCH_INPUT_SELECTOR)
        : null;

      if (focusMenuElement(previousItem ?? searchInput)) {
        event.preventDefault();
      }
    }
  }, [externalMenuListOnKeyDown]);

  return (
    <PrismThemeProvider>
      <PrimaryNavigationMenuRoot
        menuMaxWidth={menuMaxWidth}
        menuMinWidth={menuMinWidth}
        menuWidth={menuWidth}
        zoomLevel={zoomLevel}
        anchorEl={anchorEl}
        anchorOrigin={resolvedAnchorOrigin}
        anchorPosition={resolvedAnchorPosition}
        anchorReference={resolvedAnchorReference}
        open={open}
        PaperProps={{
          ...paperProps,
          ref: handlePaperRef,
          'data-primary-navigation-menu-scroll-above-label': scrollHintAboveLabel,
          'data-primary-navigation-menu-scroll-below-label': scrollHintBelowLabel,
        } as PrimaryNavigationMenuProps['PaperProps']}
        transformOrigin={resolvedTransformOrigin}
        {...props}>
        {headerChildren}
        <MenuList
          autoFocus={autoFocus}
          autoFocusItem={!disableAutoFocusItem}
          {...menuListProps}
          ref={handleMenuListRef}
          onKeyDown={handleMenuListKeyDown}
          onScroll={handleMenuListScroll}>
          {bodyChildren}
        </MenuList>
        {footerChildren}
      </PrimaryNavigationMenuRoot>
    </PrismThemeProvider>
  );
};

export const PrimaryNavigationMenuSearch = ({
  clearAriaLabel = 'Clear search',
  disabled,
  endAdornment,
  inputProps,
  onClear,
  onKeyDown,
  startAdornment,
  value,
  ...props
}: PrimaryNavigationMenuSearchProps) => {
  const handleKeyDown: NonNullable<PrimaryNavigationMenuSearchProps['onKeyDown']> = event => {
    onKeyDown?.(event);

    if (event.defaultPrevented || event.key === ESCAPE_KEY) {
      return;
    }

    event.stopPropagation();

    const paperElement = getMenuPaper(event.currentTarget as HTMLElement);

    if (event.key === ARROW_DOWN_KEY) {
      const firstMenuItem = getFocusableMenuItems(paperElement)[0];

      if (focusMenuElement(firstMenuItem)) {
        event.preventDefault();
      }
      return;
    }

    if (event.key === ARROW_UP_KEY) {
      const actionButton = paperElement?.querySelector<HTMLElement>(PRIMARY_NAVIGATION_MENU_ACTION_SELECTOR);
      const lastMenuItem = getFocusableMenuItems(paperElement).at(-1);

      if (focusMenuElement(actionButton ?? lastMenuItem)) {
        event.preventDefault();
      }
    }
  };

  const stringValue = typeof value === 'string' || typeof value === 'number'
    ? String(value)
    : '';
  const shouldShowClearButton = Boolean(onClear && !disabled && stringValue.length > 0);
  const resolvedEndAdornment = shouldShowClearButton || endAdornment ? (
    <>
      {shouldShowClearButton && (
        <Tooltip placement="bottom" title={clearAriaLabel}>
          <PrimaryNavigationMenuSearchClearButton
            aria-label={clearAriaLabel}
            data-primary-navigation-menu-search-clear="true"
            onClick={onClear}
            onMouseDown={event => event.preventDefault()}
            type="button">
            <Close fontSize="inherit" />
          </PrimaryNavigationMenuSearchClearButton>
        </Tooltip>
      )}
      {endAdornment}
    </>
  ) : undefined;

  return (
    <PrimaryNavigationMenuSearchRoot
      data-primary-navigation-menu-search-root="true"
      disabled={disabled}
      endAdornment={resolvedEndAdornment}
      inputProps={{
        ...inputProps,
        'data-primary-navigation-menu-search-input': 'true',
      }}
      onKeyDown={handleKeyDown}
      startAdornment={startAdornment ?? (
        <PrimaryNavigationMenuSearchIcon>
          <Search />
        </PrimaryNavigationMenuSearchIcon>
      )}
      value={value}
      {...props}
    />
  );
};

(PrimaryNavigationMenuSearch as PrimaryNavigationMenuChildComponent).menuChildKind =
  PRIMARY_NAVIGATION_MENU_CHILD_KIND.search;

export const PrimaryNavigationMenuAction = forwardRef<HTMLButtonElement, PrimaryNavigationMenuActionProps>(
  ({ children, icon, onKeyDown, tone = 'action', ...props }, ref) => {
    const handleKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
      onKeyDown?.(event);

      if (event.defaultPrevented) {
        return;
      }

      const paperElement = getMenuPaper(event.currentTarget);

      if (event.key === ARROW_DOWN_KEY) {
        const searchInput = paperElement?.querySelector<HTMLInputElement>(PRIMARY_NAVIGATION_MENU_SEARCH_INPUT_SELECTOR);
        const firstMenuItem = getFocusableMenuItems(paperElement)[0];

        if (focusMenuElement(searchInput ?? firstMenuItem)) {
          event.preventDefault();
        }
        return;
      }

      if (event.key === ARROW_UP_KEY) {
        const lastMenuItem = getFocusableMenuItems(paperElement).at(-1);

        if (focusMenuElement(lastMenuItem)) {
          event.preventDefault();
        }
      }
    };

    return (
      <PrimaryNavigationMenuActionFrame>
        <PrimaryNavigationMenuActionRoot
          ref={ref}
          data-primary-navigation-menu-action="true"
          onKeyDown={handleKeyDown}
          tone={tone}
          {...props}>
          {icon && <PrimaryNavigationMenuActionIcon>{icon}</PrimaryNavigationMenuActionIcon>}
          <PrimaryNavigationMenuActionContent>{children}</PrimaryNavigationMenuActionContent>
        </PrimaryNavigationMenuActionRoot>
      </PrimaryNavigationMenuActionFrame>
    );
  }
);

PrimaryNavigationMenuAction.displayName = 'PrimaryNavigationMenuAction';

(PrimaryNavigationMenuAction as PrimaryNavigationMenuChildComponent).menuChildKind =
  PRIMARY_NAVIGATION_MENU_CHILD_KIND.action;

export const PrimaryNavigationMenuAiIcon = () => (
  <PrimaryNavigationMenuAiIconRoot aria-hidden="true" />
);

export const PrimaryNavigationMenuItemLeadingAction = ({
  children,
  isDisabled = false,
  isVisible = true,
  ...props
}: PrimaryNavigationMenuItemLeadingActionProps) => (
  <PrimaryNavigationMenuItemLeadingActionRoot
    aria-disabled={isDisabled || undefined}
    isDisabled={isDisabled}
    isVisible={isVisible}
    role="button"
    tabIndex={-1}
    {...props}>
    {children}
  </PrimaryNavigationMenuItemLeadingActionRoot>
);

export const PrimaryNavigationMenuItem = forwardRef<HTMLLIElement, PrimaryNavigationMenuItemProps>(
  (
    {
      children,
      endIcon,
      icon,
      isActive = false,
      isHeader = false,
      leadingSlot,
      selected,
      tone = 'default',
      ...props
    },
    ref
  ) => {
    const contentRef = useRef<HTMLElement | null>(null);
    const resolvedLeadingSlot = leadingSlot ?? icon;

    return (
      <OverflowTooltip measureRef={contentRef} title={getTextTooltipTitle(children)}>
        <PrimaryNavigationMenuItemRoot
          ref={ref}
          data-primary-navigation-menu-scroll-item={isHeader ? undefined : 'true'}
          data-primary-navigation-menu-item-tone={tone}
          isActive={isActive}
          isHeader={isHeader}
          selected={selected ?? isActive}
          tone={tone}
          {...props}>
          {resolvedLeadingSlot && (
            <PrimaryNavigationMenuItemLeadingSlot data-primary-navigation-menu-leading-slot="true">
              {resolvedLeadingSlot}
            </PrimaryNavigationMenuItemLeadingSlot>
          )}
          <PrimaryNavigationMenuItemContent ref={contentRef}>{children}</PrimaryNavigationMenuItemContent>
          {endIcon && <PrimaryNavigationMenuItemEndIcon>{endIcon}</PrimaryNavigationMenuItemEndIcon>}
        </PrimaryNavigationMenuItemRoot>
      </OverflowTooltip>
    );
  }
);

PrimaryNavigationMenuItem.displayName = 'PrimaryNavigationMenuItem';

export const PrimaryNavigationMenuShowMoreItem = ({
  count = 0,
  itemLabelPlural,
  itemLabelSingular,
  variant,
  ...props
}: PrimaryNavigationMenuShowMoreItemProps) => {
  const isMore = variant === 'more';
  const label = isMore
    ? `Show ${count} more ${count === 1 ? itemLabelSingular : itemLabelPlural}`
    : `Show fewer ${itemLabelPlural}`;

  return (
    <PrimaryNavigationMenuItem
      endIcon={isMore ? <ArrowDownward /> : <ArrowUpward />}
      tone="action"
      {...props}>
      {label}
    </PrimaryNavigationMenuItem>
  );
};

export const PrimaryNavigationChipSkeleton = ({
  width = 96,
}: PrimaryNavigationChipSkeletonProps) => {
  const adjustedWidth = typeof width === 'number'
    ? getCssPx(width)
    : width;

  return (
    <Skeleton
      height={getCssPx(12)}
      radius={getCssPx(4)}
      sx={getPrimaryNavigationSkeletonSx}
      variant="text"
      width={adjustedWidth}
    />
  );
};

const PrimaryNavigation = ({
  actionsSlot,
  ariaLabel = 'Primary navigation',
  brandSlot,
  contextSlot,
  items,
  onChange,
  value = false,
  zoomLevel = DEFAULT_ZOOM_LEVEL,
  ...rootProps
}: PrimaryNavigationProps) => {
  const tabsFrameRef = useRef<HTMLDivElement | null>(null);
  const tabValues = useMemo(() => items.map(item => item.value), [items]);
  const {
    setActiveIndicatorRef,
    setActiveItemRef,
  } = useActiveNavigationIndicator({
    activeValue: value,
    containerRef: tabsFrameRef,
    itemValues: tabValues,
    scrollElementSelector: PRIMARY_NAVIGATION_TABS_SCROLLER_SELECTOR,
  });

  return (
    <PrismThemeProvider>
      <PrimaryNavigationRoot
        {...rootProps}
        aria-label={ariaLabel}
        data-testid={PRIMARY_NAVIGATION_TEST_IDS.root}
        zoomLevel={zoomLevel}>
        <PrimaryNavigationBrand data-testid={PRIMARY_NAVIGATION_TEST_IDS.brand} zoomLevel={zoomLevel}>
          {brandSlot && <PrimaryNavigationBrandSlot zoomLevel={zoomLevel}>{brandSlot}</PrimaryNavigationBrandSlot>}
          <PrimaryNavigationTabsFrame ref={tabsFrameRef}>
            <PrimaryNavigationActiveIndicator ref={setActiveIndicatorRef} aria-hidden="true" />
            <PrimaryNavigationTabs value={value} onChange={onChange}>
              {items.map(item => (
                <PrimaryNavigationTab
                  key={item.value}
                  ref={setActiveItemRef(item.value)}
                  value={item.value}
                  label={(
                    <OverflowTooltip title={item.label}>
                      <PrimaryNavigationTabLabel>{item.label}</PrimaryNavigationTabLabel>
                    </OverflowTooltip>
                  )}
                  disabled={item.disabled}
                  disableRipple
                  data-testid={PRIMARY_NAVIGATION_TEST_IDS.tab(item.value)}
                  zoomLevel={zoomLevel}
                />
              ))}
            </PrimaryNavigationTabs>
          </PrimaryNavigationTabsFrame>
        </PrimaryNavigationBrand>

        {contextSlot && (
          <PrimaryNavigationContext data-testid={PRIMARY_NAVIGATION_TEST_IDS.context} zoomLevel={zoomLevel}>
            {contextSlot}
          </PrimaryNavigationContext>
        )}

        {actionsSlot && (
          <PrimaryNavigationActions data-testid={PRIMARY_NAVIGATION_TEST_IDS.actions} zoomLevel={zoomLevel}>
            {actionsSlot}
          </PrimaryNavigationActions>
        )}
      </PrimaryNavigationRoot>
    </PrismThemeProvider>
  );
};

export const PrimaryNavigationSkeleton = ({
  actionCount = 2,
  ariaLabel = 'Loading primary navigation',
  contextChipCount = 3,
  itemCount = 5,
  showSearchAction = false,
  zoomLevel = DEFAULT_ZOOM_LEVEL,
}: PrimaryNavigationSkeletonProps) => (
  <PrismThemeProvider>
    <PrimaryNavigationRoot
      aria-label={ariaLabel}
      data-testid={PRIMARY_NAVIGATION_TEST_IDS.skeleton}
      zoomLevel={zoomLevel}>
      <PrimaryNavigationBrand zoomLevel={zoomLevel}>
        <PrimaryNavigationBrandSlot zoomLevel={zoomLevel}>
          <Skeleton
            height="100%"
            radius={getCssPx(4)}
            sx={getPrimaryNavigationSkeletonSx}
            width="100%"
          />
        </PrimaryNavigationBrandSlot>
        <PrimaryNavigationSkeletonTabs zoomLevel={zoomLevel}>
          {createSkeletonItems(itemCount).map(index => (
            <Skeleton
              key={index}
              height={getCssPx(12)}
              sx={getPrimaryNavigationSkeletonSx}
              variant="text"
              width={getCssPx(index === 0 ? 82 : 68)}
            />
          ))}
        </PrimaryNavigationSkeletonTabs>
      </PrimaryNavigationBrand>

      <PrimaryNavigationContext zoomLevel={zoomLevel}>
        {createSkeletonItems(contextChipCount).map(index => (
          <PrimaryNavigationChipSkeleton
            key={index}
            width={index === 0 ? 132 : 196}
            zoomLevel={zoomLevel}
          />
        ))}
      </PrimaryNavigationContext>

      <PrimaryNavigationActions zoomLevel={zoomLevel}>
        {showSearchAction && (
          <Skeleton
            height={getCssPx(32)}
            radius={getCssPx(4)}
            sx={getPrimaryNavigationSkeletonSx}
            width={getCssPx(PRIMARY_NAVIGATION_SEARCH_BUTTON_WIDTH_PX)}
          />
        )}
        {createSkeletonItems(actionCount).map(index => (
          <Skeleton
            key={index}
            height={getCssPx(32)}
            radius={getCssPx(8)}
            sx={getPrimaryNavigationSkeletonSx}
            width={getCssPx(32)}
          />
        ))}
      </PrimaryNavigationActions>
    </PrimaryNavigationRoot>
  </PrismThemeProvider>
);

export default PrimaryNavigation;
